import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTheme } from '../../context/ThemeContext';
import { Link, useNavigate } from 'react-router-dom';
import { navigateToHelpSupport } from '../../utils/authHelpers';
import { 
  Book, 
  Code, 
  FileText, 
  Search, 
  ChevronRight, 
  ArrowLeft, 
  Zap, 
  Package,
  Settings,
  Users,
  Clock,
  MessageSquare,
  Shield,
  Server,
  Database,
  ListChecks,
  Calendar,
  HelpCircle
} from 'lucide-react';

const Documentation = () => {
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('getting-started');

  const handleHelpCenterClick = (e) => {
    e.preventDefault();
    navigateToHelpSupport(navigate);
  };

  // Fixed sidebar effect on scroll
  useEffect(() => {
    const handleScroll = () => {
      const sidebar = document.getElementById('docs-sidebar');
      if (sidebar) {
        if (window.scrollY > 100) {
          sidebar.classList.add('top-4');
          sidebar.classList.remove('top-36');
        } else {
          sidebar.classList.remove('top-4');
          sidebar.classList.add('top-36');
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const categories = [
    { id: 'getting-started', name: 'Başlangıç', icon: <Zap className="w-4 h-4" /> },
    { id: 'task-management', name: 'İş Takibi', icon: <Calendar className="w-4 h-4" /> },
    { id: 'stock-management', name: 'Stok Yönetimi', icon: <Package className="w-4 h-4" /> },
    { id: 'user-management', name: 'Kullanıcı Yönetimi', icon: <Users className="w-4 h-4" /> },
    { id: 'reporting', name: 'Raporlama', icon: <FileText className="w-4 h-4" /> },
    { id: 'api', name: 'API Referansı', icon: <Code className="w-4 h-4" /> },
    { id: 'security', name: 'Güvenlik', icon: <Shield className="w-4 h-4" /> },
    { id: 'integrations', name: 'Entegrasyonlar', icon: <Server className="w-4 h-4" /> },
    { id: 'database', name: 'Veritabanı Yapısı', icon: <Database className="w-4 h-4" /> },
    { id: 'workflows', name: 'İş Akışları', icon: <ListChecks className="w-4 h-4" /> },
  ];

  const docContents = {
    'getting-started': [
      { id: 'intro', title: 'Beetrax\'a Giriş', minutes: 5 },
      { id: 'quick-start', title: 'Hızlı Başlangıç Rehberi', minutes: 10 },
      { id: 'installation', title: 'Kurulum', minutes: 15 },
      { id: 'dashboard', title: 'Ana Panel Tanıtımı', minutes: 8 },
      { id: 'first-project', title: 'İlk Projenizi Oluşturun', minutes: 12 },
    ],
    'task-management': [
      { id: 'tasks-overview', title: 'Görev Yönetimine Genel Bakış', minutes: 7 },
      { id: 'creating-tasks', title: 'Görev Oluşturma', minutes: 5 },
      { id: 'assigning-tasks', title: 'Görev Atama', minutes: 6 },
      { id: 'task-status', title: 'Görev Durumları ve Takibi', minutes: 8 },
      { id: 'deadlines', title: 'Son Tarihler ve Hatırlatıcılar', minutes: 10 },
      { id: 'task-templates', title: 'Görev Şablonları', minutes: 12 },
    ],
    'stock-management': [
      { id: 'inventory-overview', title: 'Envanter Yönetimine Genel Bakış', minutes: 8 },
      { id: 'product-catalog', title: 'Ürün Kataloğu Oluşturma', minutes: 10 },
      { id: 'stock-tracking', title: 'Stok Seviyesi Takibi', minutes: 7 },
      { id: 'barcode-integration', title: 'Barkod/QR Kod Entegrasyonu', minutes: 15 },
      { id: 'stock-alerts', title: 'Stok Uyarıları', minutes: 6 },
      { id: 'supplier-management', title: 'Tedarikçi Yönetimi', minutes: 9 },
    ],
    'user-management': [
      { id: 'user-roles', title: 'Kullanıcı Rolleri ve İzinler', minutes: 12 },
      { id: 'adding-users', title: 'Kullanıcı Ekleme', minutes: 5 },
      { id: 'teams', title: 'Ekip Oluşturma', minutes: 8 },
      { id: 'access-control', title: 'Erişim Kontrolleri', minutes: 10 },
    ],
    'reporting': [
      { id: 'report-types', title: 'Rapor Türleri', minutes: 7 },
      { id: 'custom-reports', title: 'Özel Rapor Oluşturma', minutes: 15 },
      { id: 'data-visualization', title: 'Veri Görselleştirme', minutes: 12 },
      { id: 'export-options', title: 'Dışa Aktarma Seçenekleri', minutes: 6 },
      { id: 'scheduled-reports', title: 'Planlanmış Raporlar', minutes: 9 },
    ],
    'api': [
      { id: 'api-overview', title: 'API\'ye Genel Bakış', minutes: 10 },
      { id: 'authentication', title: 'Kimlik Doğrulama', minutes: 8 },
      { id: 'endpoints-tasks', title: 'Görev Yönetimi Endpoint\'leri', minutes: 15 },
      { id: 'endpoints-inventory', title: 'Stok Yönetimi Endpoint\'leri', minutes: 15 },
      { id: 'rate-limits', title: 'Hız Limitleri ve Kotalar', minutes: 7 },
      { id: 'webhooks', title: 'Webhook\'lar', minutes: 12 },
    ],
    'security': [
      { id: 'data-encryption', title: 'Veri Şifreleme', minutes: 8 },
      { id: 'access-security', title: 'Erişim Güvenliği', minutes: 10 },
      { id: 'compliance', title: 'Uyumluluk ve Standartlar', minutes: 12 },
      { id: 'backups', title: 'Yedekleme ve Kurtarma', minutes: 9 },
    ],
    'integrations': [
      { id: 'available-integrations', title: 'Mevcut Entegrasyonlar', minutes: 10 },
      { id: 'setup-guide', title: 'Entegrasyon Kurulum Rehberi', minutes: 15 },
      { id: 'custom-integrations', title: 'Özel Entegrasyonlar', minutes: 20 },
    ],
    'database': [
      { id: 'db-structure', title: 'Veritabanı Yapısı', minutes: 12 },
      { id: 'data-models', title: 'Veri Modelleri', minutes: 15 },
      { id: 'query-examples', title: 'Sorgu Örnekleri', minutes: 10 },
    ],
    'workflows': [
      { id: 'workflow-basics', title: 'İş Akışı Temelleri', minutes: 7 },
      { id: 'automation', title: 'Otomasyon Kuralları', minutes: 12 },
      { id: 'triggers', title: 'Tetikleyiciler', minutes: 8 },
      { id: 'actions', title: 'Eylemler', minutes: 8 },
      { id: 'workflow-templates', title: 'İş Akışı Şablonları', minutes: 10 },
    ],
  };

  const articleContent = {
    'getting-started': {
      'intro': (
        <div>
          <h2 className={`text-2xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-800'}`}>Beetrax'a Giriş</h2>
          <p className={`mb-4 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
            Beetrax, işletmelerin iş süreçlerini ve stok yönetimini optimize etmek için tasarlanmış, kapsamlı bir kurumsal yönetim platformudur. 
            Bu dokümantasyon, Beetrax'ın tüm özelliklerini etkin bir şekilde kullanmanıza yardımcı olmak için hazırlanmıştır.
          </p>
          
          <h3 className={`text-xl font-semibold mt-6 mb-3 ${isDark ? 'text-white' : 'text-gray-800'}`}>Beetrax Nedir?</h3>
          <p className={`mb-4 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
            Beetrax, iki ana bileşenden oluşur:
          </p>
          
          <ul className={`list-disc pl-6 mb-6 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
            <li className="mb-2">
              <strong>İş Takip Sistemi:</strong> Projelerinizi, görevlerinizi ve iş akışlarınızı yönetmek için kapsamlı araçlar sunar.
            </li>
            <li className="mb-2">
              <strong>Stok Yönetimi Sistemi:</strong> Envanterinizi takip etmenizi, tedarik zincirinizi yönetmenizi ve stok seviyelerinizi optimize etmenizi sağlar.
            </li>
          </ul>
          
          <h3 className={`text-xl font-semibold mt-6 mb-3 ${isDark ? 'text-white' : 'text-gray-800'}`}>Temel Özellikler</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4 mb-6">
            <div className={`p-4 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-gray-100'}`}>
              <div className="flex items-start mb-2">
                <Calendar className={`w-5 h-5 mr-2 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
                <h4 className={`font-medium ${isDark ? 'text-white' : 'text-gray-800'}`}>Görev Yönetimi</h4>
              </div>
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Projelerinizi ve görevlerinizi organize edin, öncelikler belirleyin ve ekip üyelerine atamalar yapın.
              </p>
            </div>
            
            <div className={`p-4 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-gray-100'}`}>
              <div className="flex items-start mb-2">
                <Package className={`w-5 h-5 mr-2 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
                <h4 className={`font-medium ${isDark ? 'text-white' : 'text-gray-800'}`}>Stok Takibi</h4>
              </div>
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Ürünlerinizi, stok seviyelerinizi gerçek zamanlı takip edin ve otomatik uyarılar alın.
              </p>
            </div>
            
            <div className={`p-4 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-gray-100'}`}>
              <div className="flex items-start mb-2">
                <Users className={`w-5 h-5 mr-2 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
                <h4 className={`font-medium ${isDark ? 'text-white' : 'text-gray-800'}`}>Ekip İş Birliği</h4>
              </div>
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Gerçek zamanlı iletişim, dosya paylaşımı ve iş birliği araçlarıyla ekibinizle birlikte çalışın.
              </p>
            </div>
            
            <div className={`p-4 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-gray-100'}`}>
              <div className="flex items-start mb-2">
                <FileText className={`w-5 h-5 mr-2 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
                <h4 className={`font-medium ${isDark ? 'text-white' : 'text-gray-800'}`}>Raporlama</h4>
              </div>
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Kapsamlı veri analitiği ve görselleştirme araçlarıyla işletmenizin performansını izleyin.
              </p>
            </div>
          </div>
          
          <h3 className={`text-xl font-semibold mt-6 mb-3 ${isDark ? 'text-white' : 'text-gray-800'}`}>Sonraki Adımlar</h3>
          <p className={`${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
            Beetrax platformunu daha detaylı keşfetmek için "Hızlı Başlangıç Rehberi" ile devam edebilir veya 
            sol taraftaki menüden diğer bölümlere göz atabilirsiniz.
          </p>
        </div>
      ),
      // Other articles would go here
    },
    // Other categories would go here
  };

  return (
    <div className={`min-h-screen ${isDark ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'}`}>
      {/* Header */}
      <header className={`py-4 ${isDark ? 'bg-gray-800' : 'bg-white'} shadow-md`}>
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <button 
                onClick={() => navigate('/')}
                className={`p-2 rounded-full mr-4 ${isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="flex items-center">
                <Book className={`w-6 h-6 mr-2 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
                <h1 className="text-xl font-bold">Beetrax Dokümantasyon</h1>
              </div>
            </div>
            
            <div className="relative w-64">
              <input
                type="text"
                placeholder="Dokümanlarda ara..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={`w-full py-2 pl-10 pr-4 rounded-lg ${
                  isDark 
                    ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:border-blue-500' 
                    : 'bg-gray-100 border-gray-200 text-gray-900 placeholder-gray-500 focus:border-blue-500'
                } border focus:ring-2 focus:ring-blue-500/50 outline-none`}
              />
              <div className="absolute left-3 top-2.5">
                <Search className={`w-4 h-4 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        <div className="flex flex-col md:flex-row gap-8">
          {/* Sidebar */}
          <div className="md:w-1/4">
            <div 
              id="docs-sidebar"
              className={`transition-all duration-300 md:sticky md:top-36 ${
                isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
              } border rounded-xl p-4 shadow-sm`}
            >
              <h2 className={`text-lg font-bold mb-4 pb-2 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                Dokümantasyon
              </h2>

              <nav className="space-y-1">
                {categories.map((category) => (
                  <button
                    key={category.id}
                    onClick={() => setActiveCategory(category.id)}
                    className={`w-full flex items-center py-2 px-3 rounded-lg text-left transition-colors ${
                      activeCategory === category.id
                        ? (isDark ? 'bg-blue-900/30 text-blue-400' : 'bg-blue-50 text-blue-700')
                        : (isDark ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-100')
                    }`}
                  >
                    <span className="mr-3">{category.icon}</span>
                    <span>{category.name}</span>
                    {activeCategory === category.id && (
                      <ChevronRight className="w-4 h-4 ml-auto" />
                    )}
                  </button>
                ))}
              </nav>

              <div className={`mt-6 pt-6 border-t ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                <a 
                  href="#"
                  onClick={handleHelpCenterClick}
                  className={`flex items-center py-2 px-3 rounded-lg ${
                    isDark ? 'bg-gray-700 text-gray-200 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <HelpCircle className="w-4 h-4 mr-2" />
                  <span>Yardım Merkezi</span>
                </a>
              </div>
            </div>
          </div>

          {/* Main content */}
          <div className="md:w-3/4">
            <div className={`${
              isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
            } border rounded-xl p-6 shadow-sm`}>
              {/* Category title */}
              <div className="mb-8">
                <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm ${
                  isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-700'
                }`}>
                  {categories.find(c => c.id === activeCategory)?.icon}
                  <span className="ml-2">{categories.find(c => c.id === activeCategory)?.name}</span>
                </div>
                <h1 className={`text-3xl font-bold mt-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {activeCategory === 'getting-started' ? 'Başlangıç Rehberi' : 
                   activeCategory === 'task-management' ? 'İş Takip Sistemi Dokümantasyonu' :
                   activeCategory === 'stock-management' ? 'Stok Yönetimi Dokümantasyonu' :
                   activeCategory === 'user-management' ? 'Kullanıcı Yönetimi' :
                   activeCategory === 'reporting' ? 'Raporlama Araçları' :
                   activeCategory === 'api' ? 'API Dokümantasyonu' :
                   activeCategory === 'security' ? 'Güvenlik Rehberi' :
                   activeCategory === 'integrations' ? 'Entegrasyon Kılavuzu' :
                   activeCategory === 'database' ? 'Veritabanı Referansı' :
                   'İş Akışları ve Otomasyon'}
                </h1>
              </div>

              {/* Articles list */}
              <div className="mb-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {docContents[activeCategory]?.map((article) => (
                    <motion.div
                      key={article.id}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className={`p-4 rounded-lg border cursor-pointer ${
                        isDark 
                          ? 'bg-gray-700/50 border-gray-600 hover:bg-gray-700' 
                          : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                      }`}
                    >
                      <h3 className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{article.title}</h3>
                      <div className="flex items-center mt-2">
                        <Clock className={`w-4 h-4 mr-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
                        <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                          {article.minutes} dakika okuma
                        </span>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>

              {/* Sample article content */}
              <div className={`mt-10 pt-8 border-t ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                {articleContent[activeCategory] && articleContent[activeCategory]['intro']}
              </div>
            </div>

            {/* Related Resources */}
            <div className={`mt-8 ${
              isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
            } border rounded-xl p-6 shadow-sm`}>
              <h2 className={`text-xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>İlgili Kaynaklar</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Link to="/tutorials" className={`p-4 rounded-lg border ${
                  isDark 
                    ? 'bg-gray-700/50 border-gray-600 hover:bg-gray-700' 
                    : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                }`}>
                  <div className="flex items-center mb-2">
                    <Book className={`w-5 h-5 mr-2 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
                    <h3 className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>Eğitim Videoları</h3>
                  </div>
                  <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    Adım adım video rehberleri ile Beetrax'ı öğrenin
                  </p>
                </Link>
                
                <a href="#" className={`p-4 rounded-lg border ${
                  isDark 
                    ? 'bg-gray-700/50 border-gray-600 hover:bg-gray-700' 
                    : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                }`}>
                  <div className="flex items-center mb-2">
                    <FileText className={`w-5 h-5 mr-2 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
                    <h3 className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>Örnekler</h3>
                  </div>
                  <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    Örnek projeler ve şablonlar
                  </p>
                </a>
                
                <a href="#" className={`p-4 rounded-lg border ${
                  isDark 
                    ? 'bg-gray-700/50 border-gray-600 hover:bg-gray-700' 
                    : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                }`}>
                  <div className="flex items-center mb-2">
                    <MessageSquare className={`w-5 h-5 mr-2 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
                    <h3 className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>Topluluk Forumu</h3>
                  </div>
                  <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    Sorular sorun ve diğer kullanıcılarla bağlantı kurun
                  </p>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className={`mt-12 py-8 border-t ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="mb-4 md:mb-0">
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                &copy; {new Date().getFullYear()} Beetrax. Tüm hakları saklıdır.
              </p>
            </div>
            
            <div className="flex space-x-6">
              <a href="#" onClick={handleHelpCenterClick} className={`text-sm ${isDark ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}>
                Yardım Merkezi
              </a>
              <a href="#" className={`text-sm ${isDark ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}>
                Gizlilik Politikası
              </a>
              <a href="#" className={`text-sm ${isDark ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}>
                Kullanım Şartları
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Documentation; 